/**
 * New console — the shell.
 *
 * The prototype's layout, rebuilt: its own rail, its own topbar, its own router.
 * Nothing from the classic console is rendered inside it. That is the point — the
 * classic UI stays exactly as it is, untouched and deletable in one move, and this
 * console is judged on its own.
 *
 * What the shell carries, and why each earns a place in the frame rather than on a
 * screen:
 *
 *  - The clinic picker scopes every screen at once, so it cannot live on one of them.
 *  - Ctrl+K reaches any page or clinic without crossing the rail. On a console with
 *    twenty screens and forty-six clinics that is the difference between two clicks
 *    and eight.
 *  - Badges on Triage queue and the bell are the only two numbers that decide where
 *    someone goes first, so they are visible before a screen is opened.
 *  - "Classic view" stays in the topbar. A tester who dislikes what they see must be
 *    one click from what they know; making them navigate the shell they just
 *    rejected is the wrong way round.
 */
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import useAuth from '@hooks/useAuth';
import useNotistack from '@hooks/useNotistack';
import useWindowSize from '@hooks/useWindowSize';
import { useInterfaceContext } from '@contexts/interfaceContext';
import { takeRedirectPath } from '@utils/loginRedirect';

import { navGroups, navRoutes, CHORDS, screenFor, routeTitle, routeBack, routeTrail } from './navigation';
import { roleCan, isSuperAdmin } from './capabilities';
import { rowsOf } from './api';
import { clinicList } from './refData';
import useShellCounts from './useShellCounts';
import { readPrefs, writePrefs, applyPrefs, clearRootScale, readLocked, writeLocked } from './prefs';
import CommandPalette from './components/CommandPalette';
import AccountPanel from './components/AccountPanel';
import Shortcuts from './components/Shortcuts';
import Tour, { TOUR_SEEN } from './components/Tour';
import { PullToRefresh } from './components/PullToRefresh';
import { useBackRestore } from './navCache';
import A11yPanel from './components/A11yPanel';
import BottomNav, { useDockHidden } from './components/BottomNav';
import LockScreen, { useIdleLock } from './components/LockScreen';
import {
  IconMenu, IconMoon, IconSun, IconSearch, IconBell, IconChevL, IconChevR, IconAccess, IconCompass
} from './components/Icons';
import { Skeleton, HoverTip, initialsOf } from './components/Primitives';

import './theme/tokens.css';
import './theme/base.css';
// Maps rsuite's own custom properties onto the console tokens, so the date picker
// follows the theme instead of painting its own light-blue calendar.
import './theme/rsuite.css';
// Text size, density, contrast, typeface and motion — all driven from attributes
// this component sets on the root below.
import './theme/a11y.css';

/* One button, two jobs. Below the breakpoint the rail is a drawer and the button
   opens it; above, the rail is always present and the button narrows it to icons.
   Splitting these into two controls would put a dead button on every screen at
   whichever width was not currently in use. */
/* Some clinic names run past fifty characters ("Greenlight Chiropractic and Wellness Lab
   Lake Forest"). Past this length the dropdown line is cut with an ellipsis. */
const CLINIC_NAME_MAX = 30;
const shortClinic = (name) => {
  const n = String(name || '').trim();
  return n.length > CLINIC_NAME_MAX ? `${n.slice(0, CLINIC_NAME_MAX - 1).trimEnd()}…` : n;
};

const NewAppLayout = () => {
  const { user, setUiTheme, selectedClinicId, setSelectedClinicId } = useAuth();
  const { notify } = useNotistack();
  const { width } = useWindowSize();
  const { isDarkMode, toggleDarkMode } = useInterfaceContext();
  const location = useLocation();
  /* Back and Forward bring a screen back as it was — its data and its scroll —
     instead of reloading it. See navCache.js. */
  useBackRestore(location);
  const navigate = useNavigate();

  const [navOpen, setNavOpen] = useState(false);
  const [overlay, setOverlay] = useState(null); // 'cmdk' | 'account' | 'keys'
  const [clinics, setClinics] = useState([]);
  const [prefs, setPrefs] = useState(readPrefs);
  const [tour, setTour] = useState(false);
  /* Read from storage rather than started at false: reloading the page is the
     first thing anybody tries against a lock. */
  const [locked, setLocked] = useState(readLocked);
  const rootRef = useRef(null);

  const isMobile = width < 900;
  /* A phone is driven from the bottom menu, as the classic is and as an app is: the
     same width the classic shows its BottomMenu at. Between here and the rail's
     breakpoint a tablet keeps the drawer. */
  const hasDock = width < 768;
  const dockDown = useDockHidden(hasDock, location.pathname);
  const railMini = !isMobile && prefs.railMini;
  const role = (user && user.role) || '';
  const isSuper = isSuperAdmin(role);
  const clinicId = selectedClinicId || 0;

  const allowed = useMemo(() => navRoutes.filter((r) => roleCan(role, r.cap)), [role]);
  /* What the rail and the command palette offer: `hidden` keeps a route out of both
     (Roles, on request), and a route carrying a parameter has no destination until
     something supplies the id — the palette once offered "Lead detail" and navigated to
     the literal string `/leads/:leadId`. */
  const railRoutes = useMemo(
    () => allowed.filter((r) => !r.hidden && r.id.indexOf(':') < 0 && !(r.hideFor && roleCan(role, r.hideFor))),
    [allowed, role]
  );

  /* Where "/" and an address this console does not own go. Home needs leads.view; a
     role without it (the API can issue "Staff") would be sent to Home, refused there
     and sent again without end — so the landing is the first screen the role can
     actually open. */
  const landing = useMemo(() => {
    if (allowed.some((r) => r.id === '/home')) return '/home';
    return railRoutes.length ? railRoutes[0].id : '/settings';
  }, [allowed, railRoutes]);

  /* A failed switch used to do nothing at all — no console change, no message, a button
     that simply looked broken. `PUT /users/theme/` can refuse like any other write, and
     this is the one control whose whole job is to get somebody out of here. */
  const switchToClassic = useCallback(async () => {
    const res = await setUiTheme('classic');
    if (!res || !res.isOk) {
      notify('Could not switch consoles. You are still on the new one.', 'error');
    }
  }, [setUiTheme, notify]);

  const { untriaged, unread, refreshUnread } = useShellCounts(
    clinicId, roleCan(role, 'notify.log'), roleCan(role, 'leads.triage')
  );

  /* A deep link out of an email — the missed-call follow-up points at one filtered lead —
     lands on a protected route, so AuthLayout parks the target and bounces to /login.
     Whichever console the user is on has to consume it once its own routes exist; the
     classic shell does this and the new one did not, so every emailed link opened /home
     for anyone switched to this console and the parked path was simply dropped. */
  useEffect(() => {
    const target = takeRedirectPath();
    if (target) navigate(target, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* The palette offers clinics, so the shell holds the list. One call for the
     session, and the picker in the topbar reads the same array. */
  useEffect(() => {
    if (!isSuper) return undefined;
    let live = true;
    clinicList(true).then((res) => {
      if (!live) return;
      setClinics(rowsOf(res));
    });
    return () => { live = false; };
  }, [isSuper]);

  /* The console always opens on every clinic, and the choice is not remembered
     between visits. A scope that persists is the kind of state someone sets once and
     then forgets they set: every figure afterwards is quietly one clinic's, and
     nothing on screen explains why the portfolio looks so small. Starting wide means
     the numbers are always the account's until somebody narrows them on purpose. */
  const pickClinic = useCallback((id) => {
    setSelectedClinicId(Number(id) || undefined);
  }, [setSelectedClinicId]);

  const go = useCallback((route) => { navigate(route); setNavOpen(false); }, [navigate]);

  /* First visit only, and after a beat so the rail and topbar are actually on screen —
     the tour measures real elements, and pointing at one that has not mounted yet puts
     the ring in the corner. Replayed on demand from Getting started. */
  useEffect(() => {
    let seen = true;
    try { seen = !!window.localStorage.getItem(TOUR_SEEN); } catch (e) { seen = true; }
    if (seen) return undefined;
    const t = setTimeout(() => setTour(true), 900);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const run = () => setTour(true);
    window.addEventListener('nt:tour', run);
    return () => window.removeEventListener('nt:tour', run);
  }, []);

  /* Settings asks for the appearance panel the same way Help asks for the tour: an
     event, so a screen never has to be handed the shell's state to open something. */
  useEffect(() => {
    const open = () => setOverlay('a11y');
    window.addEventListener('nt:appearance', open);
    return () => window.removeEventListener('nt:appearance', open);
  }, []);

  /* Settings, the account panel and the command palette all ask for the lock the same
     way, so none of them has to be handed this state. */
  const lock = useCallback(() => {
    setOverlay(null);
    setNavOpen(false);
    writeLocked(true);
    setLocked(true);
  }, []);

  const unlock = useCallback(() => {
    writeLocked(false);
    setLocked(false);
  }, []);

  useEffect(() => {
    window.addEventListener('nt:lock', lock);
    return () => window.removeEventListener('nt:lock', lock);
  }, [lock]);

  /* A preference changed on the Settings screen. The panel has this component's own
     state; a screen has to say so, or the console would only follow on the next load. */
  useEffect(() => {
    const onPrefs = (e) => { if (e.detail) setPrefs(e.detail); };
    window.addEventListener('nt:prefs', onPrefs);
    return () => window.removeEventListener('nt:prefs', onPrefs);
  }, []);

  useIdleLock(prefs.autoLock, locked, lock);

  /* Applied to the console root rather than <html>: the classic console is still
     installed and must stay exactly as it is. */
  useEffect(() => {
    applyPrefs(rootRef.current, prefs);
    writePrefs(prefs);
  }, [prefs]);

  /* Text size is applied to <html>, which outlives this component. Switching back to
     the classic console would otherwise leave it scaled with no control to undo it. */
  useEffect(() => clearRootScale, []);

  /* Light / dark / system.
     Dark mode itself is the shared interface context — the classic console uses the
     same flag, so it is toggled rather than set, and never reached into. What this
     console adds is the third state: on `system` it tracks the operating system for
     as long as the setting stays there, instead of only reading it once at first
     run. `light` and `dark` are honoured as written, whatever the device says. */
  useEffect(() => {
    const mode = prefs.themeMode || 'system';
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const want = () => (mode === 'system' ? mq.matches : mode === 'dark');
    const sync = () => { if (want() !== isDarkMode) toggleDarkMode(); };
    sync();
    if (mode !== 'system') return undefined;
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [prefs.themeMode, isDarkMode, toggleDarkMode]);

  /* The window's own scrollbar belongs to <html>, which sits outside this root, so it
     cannot be styled from inside .nt. The class goes on while this console is mounted
     and comes off with it, leaving the classic console's scrollbar untouched. */
  useEffect(() => {
    document.documentElement.classList.add('nt-scrollbars');
    return () => document.documentElement.classList.remove('nt-scrollbars');
  }, []);

  /* The keyboard, after the prototype's list — components/Shortcuts.jsx prints it from
     the same tables. Ctrl+K works even while typing, because a search box is exactly
     where the palette is reached from; every other key is ignored inside a field, so a
     "g" typed into a note never navigates, and while an overlay or the lock is up. The
     chord is "g" then a screen's letter, with a short window for the second key. */
  useEffect(() => {
    let chord = false;
    let chordTimer = 0;
    const onKey = (e) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) || e.target.isContentEditable;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (!locked) setOverlay((o) => (o === 'cmdk' ? null : 'cmdk'));
        return;
      }
      if (typing || overlay || locked || e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key;
      if (chord) {
        chord = false;
        clearTimeout(chordTimer);
        const hit = CHORDS[k.toLowerCase()];
        if (!hit) return;
        e.preventDefault();
        if (roleCan(role, hit.cap)) go(hit.id);
        else notify('Your role cannot open that screen.', 'info');
        return;
      }
      if (k === 'g' || k === 'G') {
        chord = true;
        chordTimer = setTimeout(() => { chord = false; }, 1200);
      } else if (k === '?') {
        e.preventDefault();
        setOverlay('keys');
      } else if (k === '/') {
        const box = document.querySelector('.nt-search .nt-input');
        if (box) { e.preventDefault(); box.focus(); }
      } else if (k === '[' && !isMobile) {
        setPrefs((p) => ({ ...p, railMini: !p.railMini }));
      } else if (k === 't' || k === 'T') {
        setPrefs((p) => ({ ...p, themeMode: isDarkMode ? 'light' : 'dark' }));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); clearTimeout(chordTimer); };
  }, [overlay, locked, role, isMobile, isDarkMode, go, notify]);

  const name = `${(user && user.first_name) || ''} ${(user && user.last_name) || ''}`.trim();
  const initials = initialsOf(name, 'U');

  const scopedClinic = clinics.find((c) => c.id === clinicId);
  const back = routeBack(location.pathname, location.state);

  /* The breadcrumb names a clinic's screen after the clinic. The list is only loaded for
     a portfolio login; a clinic admin's trail falls back to the screen's own label. */
  const trail = routeTrail(location.pathname, location.state, (id) => {
    const c = clinics.find((x) => x.id === id);
    return c ? c.name : '';
  });
  const parentCrumb = trail.slice(0, -1).filter((c) => c.to).pop();

  const badgeFor = (id) => {
    if (id === '/triage') return untriaged;
    if (id === '/notifications') return unread;
    return 0;
  };

  /* Locked: the console stops rendering rather than being covered over, so the
     patient names that were on screen are not one screenshot or one devtools panel
     away. The root stays, because the theme and text size live on it. */
  if (locked) {
    return (
      <div className={`nt${isDarkMode ? ' nt-dark' : ''}`} ref={rootRef}>
        <LockScreen onUnlock={unlock} />
      </div>
    );
  }

  return (
    <div className={`nt${isDarkMode ? ' nt-dark' : ''}`} ref={rootRef}>
      <div className={`nt-app${isMobile && navOpen ? ' navopen' : ''}${railMini ? ' mini' : ''}${hasDock ? ' has-dock' : ''}${dockDown ? ' dock-down' : ''}`}>
        <aside className="nt-rail">
          <div className="nt-brand">
            <span className="nt-brand-mark">F1</span>
            <span className="nt-brand-text">
              <b>ForeverOne</b>
              <span>{scopedClinic ? scopedClinic.name : 'Portal'}</span>
            </span>
          </div>

          <nav className="nt-nav">
            {navGroups.map((group) => {
              const items = group.items.filter((i) => !i.hidden && roleCan(role, i.cap) && !(i.hideFor && roleCan(role, i.hideFor)));
              if (!items.length) return null;
              return (
                <div key={group.group}>
                  <div className="nt-nav-label">{group.group}</div>
                  {items.map((item) => {
                    const Icon = item.icon;
                    const n = badgeFor(item.id);
                    return (
                      <HoverTip key={item.id} text={railMini ? item.label : undefined} wrapClass="nt-nav-tip">
                        <NavLink
                          to={item.id}
                          className={({ isActive }) => `nt-nav-item${isActive || back === item.id ? ' on' : ''}`}
                          onClick={() => setNavOpen(false)}
                        >
                          <Icon />
                          <span className="txt">{item.label}</span>
                          {n ? <span className="pip">{n > 99 ? '99+' : n}</span> : null}
                        </NavLink>
                      </HoverTip>
                    );
                  })}
                </div>
              );
            })}
          </nav>

          <div className="nt-who-wrap">
            <button type="button" className="nt-who" onClick={() => setOverlay('account')}>
              <span className="nt-avatar">{initials}</span>
              <span className="nt-who-text">
                <b>{name || (user && user.email) || 'Signed in'}</b>
                <span>{role}</span>
              </span>
            </button>
          </div>
        </aside>

        {isMobile && navOpen ? (
          <button type="button" className="nt-scrim" aria-label="Close menu" onClick={() => setNavOpen(false)} />
        ) : null}

        <div className="nt-main">
          <header className="nt-top">
            {/* Not on a phone: the bottom menu's More holds everything the drawer did. */}
            {!hasDock ? (
              <HoverTip text={isMobile ? 'Menu' : (railMini ? 'Expand navigation' : 'Collapse navigation')}>
                <button
                  type="button"
                  className="nt-icon-btn"
                  onClick={() => (isMobile
                    ? setNavOpen((o) => !o)
                    : setPrefs((pr) => ({ ...pr, railMini: !pr.railMini })))}
                  aria-label={isMobile ? 'Menu' : (railMini ? 'Expand navigation' : 'Collapse navigation')}
                  aria-expanded={isMobile ? navOpen : !railMini}
                >
                  <IconMenu />
                </button>
              </HoverTip>
            ) : null}
            {/* The breadcrumb. On a wide screen the whole trail on one line — the rail
                group as context, each screen above this one a link, this one as the
                title. A phone has no room for a trail beside the clinic picker, so it
                keeps the one hop that matters, back to the parent, above the title. */}
            {isMobile ? (
              <div className="nt-min0">
                {parentCrumb ? (
                  <button type="button" className="nt-crumb-back" onClick={() => go(parentCrumb.to)}>
                    <IconChevL />{parentCrumb.label}
                  </button>
                ) : null}
                <div className="nt-title">
                  {trail.length ? trail[trail.length - 1].label : routeTitle(location.pathname)}
                </div>
              </div>
            ) : (
              <nav className="nt-crumbs" aria-label="Breadcrumb">
                <ol>
                  {trail.map((c, i) => {
                    const last = i === trail.length - 1;
                    if (last) {
                      return (
                        <li key={c.to || c.label} className="cur" aria-current="page">
                          <span className="nt-title">{c.label}</span>
                        </li>
                      );
                    }
                    return (
                      <li key={c.to || c.label} className={c.to ? '' : 'grp'}>
                        {c.to
                          ? <button type="button" className="lnk" onClick={() => go(c.to)}>{c.label}</button>
                          : <span className="lnk">{c.label}</span>}
                        <IconChevR className="sep" aria-hidden="true" />
                      </li>
                    );
                  })}
                </ol>
              </nav>
            )}
            <span className="nt-spacer" />

            <button type="button" className="nt-kbar" onClick={() => setOverlay('cmdk')}>
              <IconSearch />
              <span className="txt">Search or jump to…</span>
              <span className="nt-kbd">Ctrl K</span>
            </button>

            {isSuper ? (
              <HoverTip text="Scopes every screen in this console">
                <select
                  className="nt-select nt-clinic-pick"
                  value={clinicId}
                  onChange={(e) => pickClinic(Number(e.target.value) || 0)}
                  aria-label="Clinic in scope"
                >
                  <option value={0}>All clinics{clinics.length ? ` (${clinics.length})` : ''}</option>
                  {/* A long clinic name is cut with "…" so the list stays a list rather
                      than a line wider than the screen; the full name is the option's title. */}
                  {clinics.map((c) => (
                    <option key={c.id} value={c.id} title={c.name}>{shortClinic(c.name)}</option>
                  ))}
                </select>
              </HoverTip>
            ) : null}

            {/* Sets the appearance setting rather than the shared flag directly: on
                `system` the effect above would put the theme straight back. */}
            <HoverTip text={`${isDarkMode ? 'Light' : 'Dark'} — set in Appearance to follow this device`}>
              <button
                type="button"
                className="nt-icon-btn"
                onClick={() => setPrefs((p) => ({ ...p, themeMode: isDarkMode ? 'light' : 'dark' }))}
                aria-label={isDarkMode ? 'Switch to light' : 'Switch to dark'}
              >
                {isDarkMode ? <IconSun /> : <IconMoon />}
              </button>
            </HoverTip>

            {/* Hidden on a phone, where the topbar has no room for it — the account
                panel opens the same thing. */}
            <HoverTip text="Text size, density, contrast">
              <button
                type="button"
                className="nt-icon-btn nt-a11y"
                onClick={() => setOverlay('a11y')}
                aria-label="Display and accessibility"
              >
                <IconAccess />
              </button>
            </HoverTip>

            {/* Desktop only: the tour points at the rail and the topbar, which a phone
                folds away. Help & FAQ still offers it on every width. */}
            {!isMobile ? (
              <HoverTip text="Replay the tour">
                <button
                  type="button"
                  className="nt-icon-btn"
                  onClick={() => setTour(true)}
                  aria-label="Replay the tour"
                >
                  <IconCompass />
                </button>
              </HoverTip>
            ) : null}

            {roleCan(role, 'notify.log') ? (
              <button
                type="button"
                className="nt-icon-btn nt-bell"
                /* Not zeroed on a tap. Tapping the bell reads nothing; opening an alert
                   does. A badge cleared here showed 0 on this screen while every other
                   device still showed the real count. */
                onClick={() => { refreshUnread(); go('/notifications'); }}
                aria-label={`Notifications, ${unread} unclicked`}
              >
                <IconBell />
                {unread ? <span className="dot">{unread > 99 ? '99+' : unread}</span> : null}
              </button>
            ) : null}

            <HoverTip text="Return to the console you were using before">
              <button
                type="button"
                className="nt-btn sm"
                onClick={switchToClassic}
              >
                Classic view
              </button>
            </HoverTip>

            <button
              type="button"
              className="nt-avatar"
              onClick={() => setOverlay('account')}
              aria-label="Account menu"
            >
              {initials}
            </button>
          </header>

          <main className="nt-content">
            {/* On a phone, pulling the page down reloads the screen. */}
            <PullToRefresh>
              <Suspense fallback={<Skeleton h={280} />}>
                <Routes>
                  <Route path="/" element={<Navigate to={landing} replace />} />
                  {allowed.map((r) => <Route key={r.id} path={r.id} element={screenFor(r)} />)}
                  {/* Lead drop used to be its own rail item. Traffic's clinic screen is
                      the same analysis, so old bookmarks land there rather than on Home. */}
                  <Route path="/lead-drop" element={<Navigate to="/traffic-drop" replace />} />
                  {/* A route this console does not own lands rather than dead-ending. */}
                  <Route path="*" element={<Navigate to={landing} replace />} />
                </Routes>
              </Suspense>
            </PullToRefresh>
          </main>
        </div>

        {hasDock ? (
          <BottomNav
            role={role}
            pathname={location.pathname}
            back={back}
            badgeFor={badgeFor}
            hidden={dockDown}
            onGo={go}
          />
        ) : null}
      </div>

      {overlay === 'cmdk' ? (
        <CommandPalette
          routes={railRoutes}
          clinics={isSuper ? clinics : []}
          onClose={() => setOverlay(null)}
          onGo={go}
          onPickClinic={pickClinic}
          onToggleTheme={toggleDarkMode}
          isDark={isDarkMode}
          onShortcuts={() => setOverlay('keys')}
          onLock={lock}
        />
      ) : null}

      {overlay === 'account' ? (
        <AccountPanel
          onClose={() => setOverlay(null)}
          onGo={go}
          onShortcuts={() => setOverlay('keys')}
          onA11y={() => setOverlay('a11y')}
          onLock={lock}
        />
      ) : null}

      {overlay === 'keys' ? <Shortcuts role={role} onClose={() => setOverlay(null)} /> : null}

      {tour ? <Tour onClose={() => setTour(false)} /> : null}

      {overlay === 'a11y' ? (
        <A11yPanel prefs={prefs} setPrefs={setPrefs} onClose={() => setOverlay(null)} />
      ) : null}
    </div>
  );
};

export default NewAppLayout;
